import React, { useCallback, useMemo } from 'react';
import { NavLink, NavLinkProps } from 'react-router-dom';
import { useSelector, useDispatch } from '~/redux';

import { styled, colors, breakpoints, fluidRange, respondTo, css, transitionDefault, absoluteFill, respondFrom, dimensions } from '~/styles';
import Wrapper from '~/components/layout/Wrapper';
import PageTopPanel from '~/components/layout/header/PageTopPanel';
import UserDropdown from '~/components/layout/header/UserDropdown';
import PageHeaderBranding from '~/components/layout/header/PageHeaderBranding';
import ImportantSafetyInformationModal from '~/components/modals/ImportantSafetyInformationModal';
import { openModal } from '~/redux/actions';
import PageHeaderMobileContent from './PageHeaderMobileContent';


const PageHeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 5;
`

const StyledHeader = styled.div`
  line-height: 65px;
  height: 65px;
  background: ${colors.black};
  color: ${colors.white};

  ${respondTo(breakpoints.md, css`
    line-height: 50px;
    height: 50px;
  `)}
`;

const ISIModalLink = styled.ul`
`

const StyledWrapper = styled(Wrapper)`
  height: 100%;
`

/* TODO: do styling more clear using named styled components or classes */
const StyledHeaderInner = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  font-size: 11.5px;

  button {
    color: ${colors.white} !important;
    display: inherit;
  }

  a {
    text-decoration: none;
    display: block;
    white-space: nowrap;
  }

  a, button {
    ${fluidRange('paddingLeft', '10px', '20px', 
      dimensions.mobileMenuBreakpoint, breakpoints.max)
    };
    ${fluidRange('paddingRight', '10px', '20px', 
      dimensions.mobileMenuBreakpoint, breakpoints.max)
    };
  }

  nav {
    justify-self: flex-start;
    flex-grow: 1;
    flex-basis: 100%;
  }

  ul {
    ${respondTo(
      dimensions.mobileMenuBreakpoint,
      css`
        display: none;
      `,
      )}

    > li {
      height: 100%;

      > a, > button {
        height: 100%;
        ${transitionDefault};
        transition-property: color, background-color;

        span {
          display: inline-block;
          transform: translateY(5px);
        }

        &:hover, &:active {
          background-color: rgba(255, 255, 255, 0.2);
        }

        &:hover + ul, & + ul:hover, .opened > & + ul {
          opacity: 1;
          visibility: visible;
          transform: none;
        }
      }

    }

    /* dropdown submenu */
    ${respondFrom(breakpoints.lg, css`

      ul {
        position: absolute;
        z-index: -1;
        background: rgba(0, 0, 0, 0.7);
        left: -1px;
        min-width: calc(100% + 2px);
        padding: 0;
        ${fluidRange('paddingTop', '10px', '20px', 
          dimensions.mobileMenuBreakpoint, breakpoints.max)
        };
        ${fluidRange('paddingBottom', '10px', '20px', 
          dimensions.mobileMenuBreakpoint, breakpoints.max)
        };

        ${transitionDefault};
        opacity: 0;
        visibility: hidden;
        transform: translateY(-100%);

        li {
          display: block;
        }
        a {
          line-height: 2.0;
          padding: 0;
          ${fluidRange('paddingLeft', '10px', '20px', 
            dimensions.mobileMenuBreakpoint, breakpoints.max)
          };
          ${fluidRange('paddingRight', '10px', '20px', 
            dimensions.mobileMenuBreakpoint, breakpoints.max)
          };

          span {
            transform: none;
            ${transitionDefault};
          }

          &:hover, &:active {
            background-color: rgba(0, 0, 0, 1) !important;

            span {
              transform: translateX(2px) !important;
            }
          }
        }
      }
    `)}
  }
`;

const StyledNav = styled.nav`
  display: flex;
  flex-flow: row;
  white-space: nowrap;

  ${transitionDefault};
  will-change: unset;

  &.hidden {
    opacity: 0;
    visibility: hidden;
  }

  > ul {
    > li {
      position: relative;

      > a {
        position: relative;

        &::before { // bg
          content: '';
          ${absoluteFill};
          background-color: ${colors.white};
          z-index: -1;

          ${transitionDefault};
          opacity: 0;
          transform: scaleY(0);
          transform-origin: bottom center;
          transition-timing-function: ease-out;
        }
        &::after {  // bototm line
          content: '';
          position: absolute;
          bottom: 0;
          left: 20px;
          width: calc(100% - 40px);
          height: 10px;
          background-color: ${colors.gold};

          ${transitionDefault};
          opacity: 0;
          transform: scaleY(0);
          transform-origin: bottom center;
          transition-delay: 0.15s;
          transition-timing-function: ease-out;
        }

        &.active {
          color: ${colors.black};

          &::before, &::after {
            opacity: 1;
            transform: none;
          }
        }
      }
    }
  }
`;

const MobileHeaderContent = styled(PageHeaderMobileContent)`
  ${respondFrom(dimensions.mobileMenuBreakpoint, css`
    display: none;
  `)}

  /* stick to screen's right */
  ${fluidRange(
    'marginRight',
    `-${dimensions.containerPadding.min}px`,
    `-${dimensions.containerPadding.max}px`
  )}
  ${transitionDefault};
  will-change: unset;
  &.hidden {
    opacity: 0;
    visibility: hidden;
  }
`


const StyledNavLink: React.FC<NavLinkProps> = ({ children, ...props }) => (
  <NavLink {...props} >
    <span>{children}</span>
  </NavLink>
);

const PageHeader = () => {
  const user = useSelector(state => state.user);
  const dispatch = useDispatch();
  const modalState = useSelector(state => state.modal);

  const openModal_ = useCallback((modalContent: any) =>  {
    dispatch(openModal({
      content: modalContent
    }));
  }, [dispatch])

  const hideContentForFullscreenModal = useMemo(() => (
    modalState.show && modalState.mode === 'fullscreen'
  ), [modalState]);


  return (
    <PageHeaderContainer id='header-container'>
      <PageTopPanel />
      <StyledHeader>
        <StyledWrapper>
          <StyledHeaderInner>
            <PageHeaderBranding />

            {user ? (
              <StyledNav 
                className={`
                  ${hideContentForFullscreenModal ? 'hidden' : ''}
                `}
              >
                <ul>
                  {/* <li>
                    <StyledNavLink exact={true} to='/'>RHA<sup>®</sup> Home</StyledNavLink>
                  </li>
                  <li>
                    <StyledNavLink to='/auditorium'>Auditorium</StyledNavLink>
                  </li>
                  <li>
                    <StyledNavLink to='/training'>Experiential Training</StyledNavLink>
                  </li>
                  <li id='submenu-li-resource-gallery'>
                    <StyledNavLink to='/resources'>Resource Gallery</StyledNavLink>
                    <ul>
                      <li>
                        <StyledNavLink to='/resources/gallery'>All</StyledNavLink>
                      </li>
                      <li>
                        <StyledNavLink to='/resources/gallery?filters=Favorites'>Favorites</StyledNavLink>
                      </li>
                      <li>
                        <StyledNavLink to='/resources/gallery?filters=Document'>Documents</StyledNavLink>
                      </li>
                      <li>
                        <StyledNavLink to='/resources/gallery?filters=Video'>Videos</StyledNavLink>
                      </li>
                      <li>
                        <StyledNavLink to='/resources/gallery?filters=Link'>Links</StyledNavLink>
                      </li>
                    </ul>
                  </li> */}
                  <li id='submenu-li-resource-gallery'>
                    <StyledNavLink to='/resources'>Resources</StyledNavLink>
                    <ul>
                      <li>
                        <StyledNavLink to='/resources/gallery'>All</StyledNavLink>
                      </li>
                      <li>
                        <StyledNavLink to='/resources/gallery?filters=Favorites'>Favorites</StyledNavLink>
                      </li>
                      <li>
                        <StyledNavLink to='/resources/gallery?filters=Document'>Documents</StyledNavLink>
                      </li>
                      <li>
                        <StyledNavLink to='/resources/gallery?filters=Video'>Videos</StyledNavLink>
                      </li>
                      <li>
                        <StyledNavLink to='/resources/gallery?filters=Link'>Links</StyledNavLink>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <StyledNavLink to='/auditorium'>Auditorium</StyledNavLink>
                  </li>
                </ul>
              </StyledNav>
            ) : null}

            <ISIModalLink>
              <li>
                <a href='#!'
                  onClick={() => openModal_(<ImportantSafetyInformationModal />)}
                >
                  <span>Important Safety Information</span>
                </a>
              </li>
            </ISIModalLink>

            {user ? <UserDropdown /> : undefined}

            <MobileHeaderContent className={`
              ${hideContentForFullscreenModal ? 'hidden' : ''}
            `} />
          </StyledHeaderInner>
        </StyledWrapper>
      </StyledHeader>
    </PageHeaderContainer>
  );
};

export default PageHeader;



// WEBPACK FOOTER //
// ./src/components/layout/header/PageHeader.tsx